import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Stack, Typography } from '@mui/material';

import { notify } from 'components/Common/Toast';
import Modal from 'components/Common/Modal';
import Button from 'components/Common/Button';
import Autocomplete from 'components/Common/Autocomplete';
import CardWrapper from 'components/Common/CardWrapper';
import MuiLoader from 'components/Common/Loader';
import TransactionDetails from 'pages/Community/TransactionDetails';
import { AppDispatch, RootState } from 'redux/store';
import { clearWalletInvoiceDetail, fetchWalletInvoiceDetail } from 'redux/slices/wallet/WalletInvoiceDetailSlice';
import { approvePaymentRequest, cancelPaymentRequest, declinePaymentRequest } from 'services/paymentRequestsApi';
import { listMyPaymentMethods, pickPreferredCard } from 'services/paymentMethodsApi';
import { sendInvoice } from 'services/sendInvoice';
import { effectiveRoleForParish, getProblemDetail, hasAtLeastRole } from 'api/v1Helpers';
import type { SavedCard } from 'types/paymentMethods';
import type { PaymentRequestStatus } from 'types/paymentRequests';
import routes from 'constants/routes';
import { getDateAndTime, isInvoiceReceiver } from 'utils/helpers';

/** Resend creates a NEW PaymentRequest — never for PENDING (still open) or PAID (already collected). */
function canResendStatus(status: PaymentRequestStatus | undefined): boolean {
    return !!status && !['PENDING', 'PAID'].includes(status);
}

/**
 * /v1/me and PaymentRequest.senderId/recipientId share the same UUID user id space.
 * Legacy UserInfo types `id` as number; runtime /me returns a string UUID — normalize via String().
 */
function selectCurrentUserId(state: RootState): string {
    const raw = state.user.userResponse?.id ?? state.user.userInfo?.id;
    return raw != null && raw !== '' ? String(raw) : '';
}

/**
 * GET /payment-requests/:id returns 404 for both missing and unauthorized (not sender/recipient)
 * — backend never leaks existence via 403. Keep a defensive 403 branch for other gate failures.
 */
function loadErrorTitle(status: number | null, message: string): string {
    if (status === 404 || /not found/i.test(message)) return 'Payment request not found';
    if (status === 403 || /forbidden|access denied|insufficient/i.test(message)) {
        return 'You do not have access to this payment request';
    }
    if (message === 'Network Error' || status === null) {
        return 'Unable to load payment request';
    }
    return 'Unable to load payment request';
}

export default function PaymentRequestDetail() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id = '' } = useParams();
    const { request, loading, error } = useSelector((state: RootState) => state.wallet.walletInvoiceDetails);
    const currentUserId = useSelector(selectCurrentUserId);
    const myParishes = useSelector((state: RootState) => state.user.myParishes || []);
    const staffRoles = useSelector((state: RootState) => state.user.staffRoles || []);
    const selectedParishId = useSelector((state: RootState) => state.user.selectedParishId);
    const selectedParishName = useSelector((state: RootState) => state.user.selectedParishName);

    const [actionLoading, setActionLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendOpen, setResendOpen] = useState(false);
    const [declineOpen, setDeclineOpen] = useState(false);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    const [cards, setCards] = useState<SavedCard[]>([]);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState<string>('');

    useEffect(() => {
        if (!id) return;
        setResendOpen(false);
        setApproveOpen(false);
        setDeclineOpen(false);
        setCancelOpen(false);
        setActionLoading(false);
        setResendLoading(false);
        dispatch(fetchWalletInvoiceDetail(String(id)));
        return () => {
            dispatch(clearWalletInvoiceDetail());
        };
    }, [dispatch, id]);

    const myRole = useMemo(() => {
        if (!request || !currentUserId) return '';
        if (request.senderId === currentUserId) return 'SENDER';
        if (request.recipientId === currentUserId) return 'RECIPIENT';
        return '';
    }, [request, currentUserId]);

    const isReceiver = isInvoiceReceiver(myRole);
    const isPending = request?.status === 'PENDING';

    // A same-parish CLERK+ staff member who is neither sender nor recipient (myRole === '')
    // can still cancel a PENDING invoice — the backend already allows it (2a27b05); this is
    // purely the client-side gate catching up. Does NOT extend to Approve/Decline (payer-only)
    // or Resend (§7 of the ticket flags that as a separate, undecided product call).
    const isStaffOfRequestParish = useMemo(() => {
        if (!request?.parishId) return false;
        return hasAtLeastRole(effectiveRoleForParish(staffRoles, request.parishId), 'CLERK');
    }, [request?.parishId, staffRoles]);

    const parishLabel = useMemo(() => {
        if (!request?.parishId) return '';
        const fromMembership = myParishes.find((p) => p.parishId === request.parishId);
        if (fromMembership?.name) return fromMembership.name;
        if (selectedParishId === request.parishId && selectedParishName) return selectedParishName;
        return 'Parish';
    }, [request?.parishId, myParishes, selectedParishId, selectedParishName]);

    const refresh = useCallback(() => {
        if (id) dispatch(fetchWalletInvoiceDetail(String(id)));
    }, [dispatch, id]);

    const loadCards = useCallback(async () => {
        setCardsLoading(true);
        try {
            const page = await listMyPaymentMethods();
            const items = page.items || [];
            setCards(items);
            const preferred = pickPreferredCard(items);
            setSelectedCardId(preferred?.id || '');
        } catch (err) {
            notify(getProblemDetail(err) || 'Failed to load payment methods', 'error');
            setCards([]);
            setSelectedCardId('');
        } finally {
            setCardsLoading(false);
        }
    }, []);

    const openApprove = async () => {
        setApproveOpen(true);
        await loadCards();
    };

    const handleApprove = async () => {
        if (!id || !selectedCardId || actionLoading) return;
        setActionLoading(true);
        try {
            // Backend: Stripe idempotency key is `preq:<paymentRequestId>` (no client header).
            const result = await approvePaymentRequest(id, { paymentMethodId: selectedCardId });
            notify(
                `Payment submitted (status: ${result.status}). Final paid status is confirmed when Stripe completes the charge.`,
                'success'
            );
            setApproveOpen(false);
            refresh();
        } catch (err) {
            notify(getProblemDetail(err) || 'Failed to approve payment request', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDecline = async () => {
        if (!id || actionLoading || !isPending) return;
        setActionLoading(true);
        try {
            await declinePaymentRequest(id);
            notify('Payment request declined', 'success');
            setDeclineOpen(false);
            refresh();
        } catch (err) {
            notify(getProblemDetail(err) || 'Failed to decline payment request', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!id || actionLoading || !isPending) return;
        setActionLoading(true);
        try {
            await cancelPaymentRequest(id);
            notify('Payment request canceled', 'success');
            setCancelOpen(false);
            refresh();
        } catch (err) {
            notify(getProblemDetail(err) || 'Failed to cancel payment request', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    /** Resend creates a NEW PaymentRequest — never PENDING or PAID. */
    const openResendConfirm = () => {
        if (!request || !canResendStatus(request.status) || resendLoading) return;
        setResendOpen(true);
    };

    const handleConfirmResend = async () => {
        if (!request || !canResendStatus(request.status) || resendLoading) return;
        setResendLoading(true);
        try {
            const result = await dispatch(
                sendInvoice({
                    parishId: request.parishId,
                    recipientId: request.recipientId,
                    amountCents: request.amountCents,
                    subject: request.subject,
                })
            );
            if (sendInvoice.fulfilled.match(result)) {
                setResendOpen(false);
                navigate(routes.paymentRequests.detail.replace(':id', result.payload.id));
            }
        } finally {
            setResendLoading(false);
        }
    };

    // Pending recipient: Decline + Approve. Pending sender: Cancel only.
    // Resend: sender only, and only DECLINED / CANCELED / EXPIRED (never PENDING or PAID).
    const showPendingRecipientActions = !!myRole && isPending && isReceiver;
    const showPendingSenderCancel = isPending && !isReceiver && (!!myRole || isStaffOfRequestParish);
    // Resend stays sender-only for now — whether same-parish staff may resend a colleague's
    // invoice is an explicit, undecided product call (ticket §7), not silently widened here.
    const showSenderResendOnly = !!myRole && !isReceiver && canResendStatus(request?.status);
    const cardOptions = cards.map((c) => ({
        value: c.id,
        label: `${(c.brand || 'Card').toUpperCase()} •••• ${c.last4 || '????'}${c.isDefault ? ' (default)' : ''}`,
    }));

    if (!loading && error) {
        return (
            <CardWrapper>
                <Alert severity={error.status === 403 ? 'warning' : 'error'} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                        {loadErrorTitle(error.status, error.message)}
                    </Typography>
                    <Typography variant="body2">{error.message}</Typography>
                </Alert>
                <Stack direction="row" justifyContent="center">
                    <Button title="Back" width="100%" variant="outlined" onClick={() => navigate(routes.home.main)} />
                </Stack>
            </CardWrapper>
        );
    }

    // Boot / in-flight: loader only — never mount TransactionDetails with empty/$0 money fields.
    if (loading || (!request && !error)) {
        return (
            <CardWrapper>
                <MuiLoader loading />
            </CardWrapper>
        );
    }

    if (!request) {
        return (
            <CardWrapper>
                <Alert severity="info">Payment request not found.</Alert>
            </CardWrapper>
        );
    }

    return (
        <>
            <TransactionDetails
                loading={false}
                showButtons={showPendingRecipientActions}
                cancelButtonProps={{
                    loading: actionLoading,
                    onClick: () => setDeclineOpen(true),
                    disabled: !isPending || actionLoading,
                    title: 'Decline',
                }}
                submitButtonProps={{
                    loading: actionLoading,
                    onClick: openApprove,
                    disabled: !isPending || actionLoading,
                    title: 'Approve & Pay',
                }}
                details={{
                    confirmationCode: request.id,
                    transactionDateTime: request.createdAt || '',
                    balance: request.amountCents || 0,
                    description: request.subject || '',
                    username: '',
                    logoUrl: '',
                    status: request.status || '',
                    myRole,
                    name: '',
                }}
            />

            {showPendingSenderCancel && (
                <Stack direction="row" justifyContent="center" sx={{ mb: 2, px: { xs: 2, lg: '20vh' } }}>
                    <Button
                        width="100%"
                        title="Cancel request"
                        loading={actionLoading}
                        disabled={!isPending || actionLoading}
                        onClick={() => setCancelOpen(true)}
                        variant="outlined"
                    />
                </Stack>
            )}

            {showSenderResendOnly && (
                <Stack direction="row" justifyContent="center" sx={{ mb: 4, px: { xs: 2, lg: '20vh' } }}>
                    <Button
                        width="100%"
                        title="Resend as new request"
                        loading={resendLoading}
                        disabled={resendLoading || !canResendStatus(request.status)}
                        onClick={openResendConfirm}
                    />
                </Stack>
            )}

            <Typography sx={{ textAlign: 'center', mt: 1, px: 2 }} color="text.secondary" variant="body2">
                {parishLabel} · Currency {(request.currency || '').toUpperCase()} · Updated{' '}
                {getDateAndTime(request.statusAt || request.createdAt)}
            </Typography>

            {!myRole && !showPendingSenderCancel && (
                <Typography sx={{ textAlign: 'center', mt: 2 }} color="text.secondary">
                    You can view this request but have no actions (you are not the sender or recipient).
                </Typography>
            )}

            <Modal onClose={() => !actionLoading && setDeclineOpen(false)} isOpen={declineOpen} modalTitle="Confirm Decline">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>Decline this payment request? This cannot be undone.</Typography>
                    <Typography sx={{ fontSize: { xs: 12, sm: 14 }, mt: 1, color: 'text.secondary' }}>
                        The sender will be notified. You will not be charged.
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button
                            onClick={() => setDeclineOpen(false)}
                            width="45%"
                            title="Back"
                            disabled={actionLoading}
                            variant="outlined"
                        />
                        <Button
                            onClick={handleDecline}
                            width="45%"
                            title="Decline"
                            loading={actionLoading}
                            disabled={actionLoading || !isPending}
                        />
                    </div>
                </>
            </Modal>

            <Modal onClose={() => !actionLoading && setCancelOpen(false)} isOpen={cancelOpen} modalTitle="Confirm Cancel">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>Cancel this payment request? This cannot be undone.</Typography>
                    <Typography sx={{ fontSize: { xs: 12, sm: 14 }, mt: 1, color: 'text.secondary' }}>
                        The recipient will no longer be able to pay this request.
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button onClick={() => setCancelOpen(false)} width="45%" title="Back" disabled={actionLoading} variant="outlined" />
                        <Button
                            onClick={handleCancel}
                            width="45%"
                            title="Cancel request"
                            loading={actionLoading}
                            disabled={actionLoading || !isPending}
                        />
                    </div>
                </>
            </Modal>

            <Modal onClose={() => !resendLoading && setResendOpen(false)} isOpen={resendOpen} modalTitle="Confirm Resend">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>
                        Resend creates a <strong>new</strong> payment request for the same recipient and amount. The original request is not
                        modified.
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 12, sm: 14 }, mt: 1, color: 'text.secondary' }}>
                        Only use this when the previous request was declined, canceled, or expired — not when it is still pending or already
                        paid.
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button
                            onClick={() => setResendOpen(false)}
                            width="45%"
                            title="Cancel"
                            disabled={resendLoading}
                            variant="outlined"
                        />
                        <Button
                            onClick={handleConfirmResend}
                            width="45%"
                            title="Create new request"
                            loading={resendLoading}
                            disabled={resendLoading || !canResendStatus(request.status)}
                        />
                    </div>
                </>
            </Modal>

            <Modal onClose={() => !actionLoading && setApproveOpen(false)} isOpen={approveOpen} modalTitle="Approve payment request">
                <>
                    {cardsLoading ? (
                        <Typography>Loading saved cards…</Typography>
                    ) : cards.length === 0 ? (
                        <Stack gap={2}>
                            <Typography>
                                No saved payment methods found. Add a card in the mobile app (or another flow that saves cards via Stripe
                                SetupIntent), then try again.
                            </Typography>
                            <Button onClick={() => setApproveOpen(false)} title="Close" width="100%" />
                        </Stack>
                    ) : (
                        <Stack gap={2}>
                            <Typography>Charge your saved card. Final status becomes PAID when Stripe confirms the payment.</Typography>
                            <Autocomplete
                                label="Payment method"
                                options={cardOptions}
                                value={selectedCardId || null}
                                getOptionLabel={(o) => o?.label || ''}
                                getOptionKey={(o) => o?.value}
                                isOptionEqualToValue={(o) => o?.value === selectedCardId}
                                onChange={(_, item) => setSelectedCardId(item?.value || '')}
                            />
                            <Stack direction="row" gap={2}>
                                <Button
                                    width="50%"
                                    title="Cancel"
                                    variant="outlined"
                                    disabled={actionLoading}
                                    onClick={() => setApproveOpen(false)}
                                />
                                <Button
                                    width="50%"
                                    title="Confirm payment"
                                    loading={actionLoading}
                                    disabled={!selectedCardId || actionLoading}
                                    onClick={handleApprove}
                                />
                            </Stack>
                        </Stack>
                    )}
                </>
            </Modal>
        </>
    );
}
